<template>
  <v-layout>
    <div v-if="loading">
      <spinner />
    </div>
    <v-layout v-else column>
      <v-flex class="pb-16">
        <text-field
          v-model.trim="form.name"
          autofocus
          :validator="vs.form.name"
          :placeholder="adminText('workflowsAll.e_g_vip_ticket_regular_early_bird_etc_3805574', 'e.g. VIP Ticket, Regular, Early Bird, etc.')"
          :error-messages="{
            maxLength: adminText('workflowsAll.name_can_t_be_more_than_170_characters_dacd64a', 'Name can’t be more than 170 characters'),
          }"
          :label="adminText('workflowsAll.ticket_name_cb5eb35', 'Ticket name*')"
        />
      </v-flex>
      <div class="pb-16" style="display: flex">
        <v-select
          v-model="form.category"
          box
          :label="adminText('workflowsAll.ticket_category_f351fd1', 'Ticket category')"
          :items="workflowUi(categories)"
          style="flex: 7"
          :hint="adminText('workflowsAll.used_for_ticket_segmentation_we_recommend_mor_75ec608', 'Used for ticket segmentation. We recommend more than 1 category.')"
          persistent-hint
          :disabled="categories.length === 0"
        >
          <template v-slot:selection="{ item }">
            <div class="color" :style="{ background: item.hexadecimal }" />
            {{ workflowLabel(item.name) }}
          </template>
          <template v-slot:item="{ item }">
            <div class="color" :style="{ background: item.hexadecimal }" />
            {{ workflowLabel(item.name) }}
          </template>
        </v-select>
        <main-button flat @click.prevent="handleOrganizationItemCategoryModalState">
          {{ adminText('workflowsAll.create_category_61495a8', 'Create category') }}
        </main-button>
      </div>
      <v-layout class="pb-8">
        <text-field
          v-model.trim="form.price"
          type="number"
          step="0.01"
          :disabled="isSoldOut"
          :suffix="currency"
          :onwheel="true"
          :validator="vs.form.price"
          :error-messages="{
            minValue: adminText('workflowsAll.price_must_be_higher_than_0_a3c4110', 'Price must be higher than 0.'),
          }"
          :label="adminText('workflowsAll.price_ebb41ba', 'Price*')"
        />
      </v-layout>
      <floor-plan-seat-color-picker
        v-if="event.extraModules && event.extraModules.floorPlan"
        v-model="form.color"
        :label="workflowLabel(floorPlanColorLabel)"
      />
      <p v-if="isSoldOut" class="price-disabled-description">
        {{ adminText('workflowsAll.price_cannot_be_changed_after_a_ticket_has_be_205dd4f', 'Price cannot be changed after a ticket has been sold at least once.') }}
      </p>
      <div
        v-if="organization.type === OrganizationType.SPORT_ORGANIZATION"
        class="sport-membership-ticket-container"
      >
        <v-checkbox
          v-model="form.itemMeta.sportMembershipTicket"
          :label="adminText('workflowsAll.ticket_valid_only_for_club_members_804c26b', 'Ticket valid only for club members.')"
          style="margin: 0 !important; padding: 0 !important"
        />
      </div>
      <div
        v-if="
          [TicketType.TABLE, TicketType.PARTER].includes(ticketType) ||
            !event.extraModules.floorPlan
        "
        style="display: flex; gap: 1rem; margin-bottom: 1rem"
      >
        <text-field
          v-model.number="form.itemMeta.min"
          type="number"
          step="1"
          :validator="vs.form.itemMeta.min"
          :error-messages="{
            minValue: adminText('workflowsAll.quantity_must_be_higher_than_0_979e8d5', 'Quantity must be higher than 0.'),
          }"
          :label="adminText('workflowsAll.per_order_min_2e89496', 'Per order min*')"
          :hint="adminText('workflowsAll.minimum_of_tickets_a_customer_must_choose_whe_3edaa96', 'Minimum of tickets a customer must choose when purchasing.')"
          persistent-hint
        />
        <text-field
          v-model.number="form.itemMeta.max"
          type="number"
          step="1"
          :validator="vs.form.itemMeta.max"
          :error-messages="{
            minValue: adminText('workflowsAll.quantity_must_be_higher_than_0_979e8d5', 'Quantity must be higher than 0.'),
          }"
          :label="adminText('workflowsAll.per_order_max_56f74fa', 'Per order max*')"
          :hint="workflowLabel(itemMetaMaxHint)"
          persistent-hint
        />
      </div>
      <v-flex
        v-if="!event.extraModules.floorPlan || ticketType === TicketType.PARTER"
        class="pb-16"
      >
        <text-field
          v-model.number="form.initialQuantity"
          type="number"
          :validator="vs.form.initialQuantity"
          :label="adminText('workflowsAll.initial_quantity_42dcebf', 'Initial quantity*')"
          :onwheel="true"
          :hint="adminText('workflowsAll.amount_of_tickets_that_will_be_available_for__17a6dad', 'Amount of tickets that will be available for sale.')"
          persistent-hint
          :error-messages="{
            maxValue:
              maxInitialQuantity === 0
                ? adminText('workflowsAll.you_cannot_add_any_new_tickets_because_total__38330e9', 'You cannot add any new tickets because total tickets number already exceeded event capacity.')
                : adminText('workflowsAll.total_quantity_exceeds_event_capacity_you_can_685892a', 'Total quantity exceeds event capacity. You can set the quantity to a maximum of {{count}}.', { count: maxInitialQuantity }),
            minValue: adminText('workflowsAll.quantity_must_be_higher_than_0_979e8d5', 'Quantity must be higher than 0.'),
            validateInitialQuantity: adminText('workflowsAll.quantity_must_be_bigger_than_number_of_alread_78c69fd', 'Quantity must be bigger than number of already sold tickets!'),
          }"
        />
      </v-flex>
      <v-layout class="pb-8" style="gap: 1rem">
        <v-flex xs12>
          <datetime-field
            v-model="form.from"
            :validator="vs.form.from"
            :label="adminText('workflowsAll.start_date_2fde03a', 'Start date*')"
            :min="new Date(new Date().getTime()).toString()"
            :max="form.to ? form.to.toString() : null"
            :hint="adminText('workflowsAll.start_date_of_sale_of_this_ticket_type_to_sta_9e45afe', 'Start date of sale of this ticket type. To start sale immediately, set it to current time.')"
            :error-messages="{
              minDate: adminText('workflowsAll.start_date_must_be_a_future_date_fd9430e', 'Start date must be a future date'),
              maxDate: adminText('workflowsAll.start_date_should_be_before_the_end_of_the_ev_ec2ed4d', 'Start date should be before the end of the event'),
            }"
          />
        </v-flex>
        <v-flex xs12 style="width: 100%">
          <datetime-field
            v-model="form.to"
            :validator="vs.form.to"
            :label="adminText('workflowsAll.end_date_dc7fcbd', 'End date*')"
            :hint="adminText('workflowsAll.end_date_of_sale_of_this_ticket_type_74495a9', 'End date of sale of this ticket type.')"
            :min="
              form.from
                ? form.from.toString()
                : new Date(new Date().getTime() + 1000 * 60 * 5).toString()
            "
            :error-messages="{
              minDate: adminText('workflowsAll.end_date_should_not_be_before_the_start_of_th_019643c', 'End date should not be before the start of the event'),
            }"
          />
        </v-flex>
      </v-layout>
      <div class="pb-16">
        <div style="display: flex; align-items: center; justify-content: space-between">
          <p style="margin: 0" class="entry-date-title">
            {{ adminText('workflowsAll.entry_with_this_ticket_is_restricted_by_date__e262725', 'Entry with this ticket is restricted by date range') }}
          </p>
          <v-switch
            v-model="form.hasDateValidationRange"
            style="flex: 0 1 0%"
            :validator="vs.form.hasDateValidationRange"
          />
        </div>
        <p class="entry-date-description">
          {{ adminText('workflowsAll.this_enables_you_to_limit_when_tickets_can_be_eabc89b', 'This enables you to limit when tickets can be scanned for entry to the event using the CoreEvent Scanner app. For example, for a festival with tickets for Friday and Saturday, enabling this ensures that Friday tickets can only be scanned on Friday, and Saturday tickets only on Saturday. The valid dates for each ticket will be printed on the ticket.') }}
        </p>
        <div v-if="form.hasDateValidationRange">
          <div style="display: flex; align-items: center">
            <v-checkbox v-model="isSingleDay" :label="adminText('workflowsAll.single_day_0af1a3d', 'Single Day')" />
            <v-checkbox v-model="isMultipleDays" :label="adminText('workflowsAll.multiple_days_2f4458c', 'Multiple Days')" />
          </div>
          <div
            style="display: flex; align-items: center; justify-content: space-between; gap: 24px"
          >
            <datetime-field
              v-model="form.entryFrom"
              style="width: 50%"
              :label="adminText('workflowsAll.start_date_976276c', 'Start Date*')"
              :validator="vs.form.entryFrom"
              :max="form.entryTo ? form.entryTo.toString() : event.to.toString()"
              :min="new Date().toString()"
            />
            <datetime-field
              v-model="form.entryTo"
              :disabled="!form.entryFrom"
              style="width: 50%"
              :label="adminText('workflowsAll.end_date_ab621d2', 'End Date*')"
              :validator="vs.form.entryTo"
              :min="form.entryFrom ? form.entryFrom.toString() : null"
              :max="event.to.toString()"
            />
          </div>
        </div>
      </div>
      <v-flex class="pb-16">
        <textarea-field
          v-model.trim="form.description"
          :validator="vs.form.description"
          :label="adminText('workflowsAll.ticket_description_e0c73b5', 'Ticket description')"
          :hint="adminText('workflowsAll.ticket_description_visible_to_customers_when__ac5dcc9', 'Ticket description visible to customers when buying a ticket.')"
        />
      </v-flex>
      <div v-if="!event.extraModules.floorPlan">
        <div class="outlets-container-wrapper">
          <div
            class="outlets-container"
            :style="{
              backgroundColor: form.digitalOutlets ? '#E6F1F8' : 'white',
              borderColor: form.digitalOutlets ? '#0277BD' : '#D9D9D9',
            }"
          >
            <v-checkbox
              v-model="form.digitalOutlets"
              :label="adminText('workflowsAll.digital_outlets_a32c9d9', 'Digital outlets')"
              :color="'#0277BD'"
              class="outlets-title"
              :style="{
                alignSelf: 'flex-start',
                color: form.digitalOutlets ? '#0277BD' : '#333333',
              }"
              @click="$set(form, 'digitalOutlets', true)"
            />
            <div class="outlets-items-wrapper" style="height: 100%">
              <div class="outlets-item">
                <img src="../../../public/assets/web-shop.svg" alt="info">
                <p class="outlets-text" style="margin: 0">{{ adminText('workflowsAll.web_shop_edd5715', 'Web shop') }}</p>
              </div>
              <div class="outlets-item">
                <img src="../../../public/assets/kiosk-terminals.svg" alt="info">
                <p class="outlets-text" style="margin: 0">
                  {{ adminText('workflowsAll.kiosk_terminals_f893db7', 'Kiosk terminals') }}
                  <v-icon
                    color="#0277BD"
                    size="16"
                    style="margin-left: 4px; cursor: pointer"
                    @click="openKioskTerminalsInfo"
                  >
                    mdi-information-outline
                  </v-icon>
                </p>
              </div>
            </div>
          </div>
          <div
            class="outlets-container"
            :style="{
              backgroundColor: form.physicalOutlets ? '#E6F1F8' : 'white',
              borderColor: form.physicalOutlets ? '#0277BD' : '#D9D9D9',
            }"
            @click="handlePhysicalOutletsClick"
          >
            <v-checkbox
              v-model="form.physicalOutlets"
              :label="adminText('workflowsAll.physical_outlets_be785a4', 'Physical outlets')"
              :color="'#0277BD'"
              class="outlets-title"
              :style="{
                alignSelf: 'flex-start',
                color: form.physicalOutlets ? '#0277BD' : '#333333',
              }"
              @click="handlePhysicalOutletsClick"
            />
            <div class="outlets-item">
              <img src="../../../public/assets/shops.svg" alt="info">
              <p class="outlets-text" style="margin: 0">{{ adminText('workflowsAll.shops_ac06f65', 'Shops') }}</p>
            </div>
          </div>
        </div>
        <v-flex class="pb-16">
          <v-select
            v-if="form.physicalOutlets"
            ref="selectSellers"
            v-model="selectedSellers"
            :items="workflowUi(availableSellers)"
            :menu-props="{ offsetY: true }"
            chips
            item-text="name"
            item-value="id"
            :label="adminText('workflowsAll.select_outlets_c18d2bc', 'Select Outlets')"
            :hint="
              workflowLabel(tisak && form.sellers && form.sellers.length && form.sellers.includes(tisak.id)
                ? adminText('workflowsAll.tisak_outlets_will_charge_ticket_buyers_an_ad_37235cd', 'TISAK outlets will charge ticket buyers an additional ticket fee')
                : '')
            "
            persistent-hint
            multiple
            @change="handleSelectChange"
          >
            <template v-slot:prepend-item>
              <div class="select-all-wrapper" @click="onSelectAllClick">
                <div class="select-all-item" style="padding: 8px; cursor: pointer">
                  <div class="select-all-title">{{ adminText('workflowsAll.select_all_86a599e', 'Select All') }}</div>
                </div>
              </div>
              <v-divider />
            </template>

            <template v-slot:selection="{ item }">
              <v-chip color="white" text-color="#0277BD" close @input="deselectSeller(item)">
                {{ workflowLabel(item.name) }}
              </v-chip>
            </template>
          </v-select>
          <kiosk-terminals-info-modal
            v-if="openKioskTerminalsInfoModal"
            :show.sync="openKioskTerminalsInfoModal"
          />
          <p v-if="Number(form.price) === 0" class="tisak-term pt-2">
            {{ adminText('workflowsAll.the_price_of_the_ticket_must_be_greater_than__56600ce', 'The price of the ticket must be greater than 1 euro to be able to add ticket sellers.') }}
          </p>
          <checkbox v-model="form.showIfSold" :color="'#0277BD'" class="display-flag-checkbox">
            <div>{{ adminText('workflowsAll.display_the_ticket_in_the_store_if_sold_out_o_2956d04', 'Display the ticket in the store if sold out or has expired') }}</div>
          </checkbox>
        </v-flex>
      </div>
      <div v-if="showAccessCodeSettings">
        <v-flex class="access-code-container">
          <div class="access-code-text">
            <h3 class="access-code-title">{{ adminText('workflowsAll.ticket_access_code_2b3e938', 'Ticket Access Code') }}</h3>
            <p class="access-code-description">
              {{ workflowLabel(accessCodeDescription) }}
            </p>
          </div>
          <v-switch
            v-model="useAccessCode"
            hide-details
            color="#0277BD"
            class="access-code-switch pa-0 ma-0"
            style="flex: 0"
            @change="onUseAccessCodeChange"
          />
        </v-flex>
        <v-expand-transition>
          <text-field
            v-if="useAccessCode"
            v-model.trim="form.itemMeta.accessCode"
            :validator="vs.form.itemMeta.accessCode"
            :label="adminText('workflowsAll.ticket_access_code_c686c08', 'Ticket Access Code*')"
            :placeholder="adminText('workflowsAll.enter_code_4_8_characters_b8d9511', 'Enter code (4-8 characters)')"
            :hint="adminText('workflowsAll.the_code_must_be_between_4_and_8_characters_l_e37df01', 'The code must be between 4 and 8 characters long and can include letters and numbers.')"
            persistent-hint
            class="access-code-field"
            :error-messages="{
              alphaNum: adminText('workflowsAll.access_code_can_only_contain_letters_and_numb_96dc695', 'Access code can only contain letters and numbers.'),
              minLength: adminText('workflowsAll.access_code_must_be_at_least_4_characters_lon_31f78a5', 'Access code must be at least 4 characters long.'),
              maxLength: adminText('workflowsAll.the_access_code_can_be_no_more_than_8_charact_7671b64', 'The access code can be no more than 8 characters long.'),
            }"
          />
        </v-expand-transition>
      </div>
      <manage-ticket-categories-modal
        v-if="openModal"
        :show.sync="openModal"
        :initial-values="organizationItemCategory"
        :colors="colors"
        :priorities="priorities"
        :categories="categories"
        @values="organizationItemCategory = $event"
        @close="handleOrganizationItemCategoryModalState"
        @create-category="handleCreateCategory($event)"
      />
    </v-layout>
    <simple-snackbar :open.sync="snackbar.open" :message-prop="workflowLabel(snackbar.messageProp)" :color="snackbar.color" />
  </v-layout>
</template>

<script>
import WorkflowDisplay from '@/admin-i18n/workflows-all-display'
import { GET_TICKET_SELLERS } from '@/graphql/user.graphql'
import { exchangeRate, tisakTicketSeller } from '@/utils'
import { TicketType, ValidityType, OrganizationType } from '@/types'
import {
  CREATE_ORGANIZATION_ITEM_CATEGORY,
  GET_ORGANIZATION_ITEM_CATEGORIES,
} from '@/graphql/organizationItemCategory.graphql'
import ManageTicketCategoriesModal from '@/components/organizations/ticket-categories/ManageTicketCategoriesModal.vue'
import {
  CategoryColors,
  Priorities,
} from '@/components/organizations/ticket-categories/TicketsCategoriesContainer.vue'
import { required } from 'vuelidate/lib/validators'
import simpleSnackbar from '@/components/shared/snackbars/simple-snackbar.vue'
import KioskTerminalsInfoModal from '@/components/tickets/kiosk-terminals-info-modal.vue'
import FloorPlanSeatColorPicker, {
  COLLAPSED_COLORS,
} from '@/components/tickets/FloorPlanSeatColorPicker.vue'

export default {
  mixins: [WorkflowDisplay],
  name: 'ticket-form',
  components: {
    simpleSnackbar,
    ManageTicketCategoriesModal,
    KioskTerminalsInfoModal,
    FloorPlanSeatColorPicker,
  },
  props: {
    initialValues: {
      type: Object,
      default: null,
    },
    vs: {
      type: Object,
      default: () => ({}),
    },
    eventId: {
      default: null,
      type: String,
    },
    event: {
      default: null,
      type: Object,
    },
    currency: {
      default: null,
      type: String,
    },
    isSoldOut: {
      default: false,
      type: Boolean,
    },
    organizationSlug: {
      default: null,
      type: String,
    },
    ticketType: {
      type: String,
      default: TicketType.REGULAR,
    },
    maxInitialQuantity: null,
    initialUseAccessCode: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      form: {},
      promoCode: {},
      OrganizationType,
      exchangeRate: exchangeRate,
      promoCodeExists: false,
      loading: true,
      inEdit: false,
      dateRange: false,
      selectedSellers: [],
      availableSellers: [],
      tisak: {},
      categories: [],
      TicketType,
      openModal: false,
      colors: CategoryColors,
      snackbar: {},
      priorities: Priorities.map(p => p.roman),
      organizationItemCategory: {},
      useAccessCode: this.initialUseAccessCode,
      organization: {},
      openKioskTerminalsInfoModal: false,
    }
  },
  computed: {
    floorPlanColorLabel() {
      if (this.ticketType === TicketType.TABLE) return this.adminText('workflowsAll.table_ecd31c8', 'Table')
      if (this.ticketType === TicketType.PARTER) return this.adminText('workflowsAll.standing_3f31ee4', 'Standing')

      return this.adminText('workflowsAll.seat_b90b325', 'Seat')
    },
    itemMetaMaxHint() {
      if (this.form.itemMeta && this.form.itemMeta.sharedTable && this.form.itemMeta.sharedTable.enabled) {
        return this.adminText('workflowsAll.shared_table_limits_hint', 'For shared tables, max sets the seats per table. Only existing packages within min/max remain allowed.')
      }
      let text = this.adminText('workflowsAll.maximum_of_tickets_a_customer_can_choose_when_7c341ec', 'Maximum of tickets a customer can choose when purchasing.')
      if (this.ticketType !== TicketType.TABLE) {
        text += this.adminText('workflowsAll.maximum_limit_can_be_set_to_10_a920146', ' Maximum limit can be set to 10.')
      }

      return text
    },
    isSingleDay: {
      get() {
        return this.form.validityType === ValidityType.SINGLE_DAY
      },
      set(value) {
        this.form.validityType = value ? ValidityType.SINGLE_DAY : ValidityType.MULTIPLE_DAYS
      },
    },
    isMultipleDays: {
      get() {
        return this.form.validityType === ValidityType.MULTIPLE_DAYS
      },
      set(value) {
        this.form.validityType = value ? ValidityType.MULTIPLE_DAYS : ValidityType.SINGLE_DAY
      },
    },
    allSellersSelected() {
      const ids = (this.availableSellers || []).map(s => s.id)

      return ids.length > 0 && ids.every(id => (this.selectedSellers || []).includes(id))
    },
    showAccessCodeSettings() {
      if (this.isAssignedFloorPlanParterTicket) {
        return false
      }

      return !this.event.extraModules.floorPlan || this.ticketType === TicketType.PARTER
    },
    isFloorPlanParterTicket() {
      return this.event.extraModules.floorPlan && this.ticketType === TicketType.PARTER
    },
    isAssignedFloorPlanParterTicket() {
      return (
        this.isFloorPlanParterTicket &&
        Array.isArray(this.form.sectionSeats) &&
        this.form.sectionSeats.length > 0
      )
    },
    accessCodeDescription() {
      if (this.isFloorPlanParterTicket) {
        return [
          this.adminText('workflowsAll.this_ticket_is_excluded_from_the_floor_plan_c687b5c', 'This ticket is excluded from the floor plan'),
          this.adminText('workflowsAll.and_will_appear_only_as_an_additional_ticket__e2672c1', 'and will appear only as an additional ticket option.'),
        ].join(' ')
      }

      return [
        this.adminText('workflowsAll.this_ticket_will_only_be_visible_to_users_who_c81ecde', 'This ticket will only be visible to users who enter the correct access code.'),
        this.adminText('workflowsAll.availability_is_limited_to_sales_through_the__91a5c4e', 'Availability is limited to sales through the CoreEvent webshop.'),
      ].join(' ')
    },
  },
  watch: {
    'form.hasDateValidationRange'(val) {
      if (val && !this.form.validityType) {
        this.$set(this.form, 'validityType', ValidityType.SINGLE_DAY)
      }
    },
    form: {
      handler(val) {
        this.$emit('values', val)
      },
      deep: true,
    },
  },
  mounted() {
    this.form = this.initialValues || { digitalOutlets: true, physicalOutlets: false, sellers: [] }
    if (!this.form.itemMeta) {
      this.$set(this.form, 'itemMeta', {})
    }
    if (this.form.digitalOutlets === undefined) {
      this.$set(this.form, 'digitalOutlets', true)
    }
    if (this.form.physicalOutlets === undefined) {
      this.$set(this.form, 'physicalOutlets', false)
    }
    if (!this.initialValues) {
      this.$set(this.form, 'validityType', ValidityType.SINGLE_DAY)
    }
    if (this.event.extraModules && this.event.extraModules.floorPlan && !this.form.color) {
      this.$set(this.form, 'color', COLLAPSED_COLORS[0])
    }
    if (this.initialValues) {
      this.inEdit = true
      if (this.form.hasDateValidationRange) {
        this.$set(
          this.form,
          'validityType',
          this.initialValues.validityType || ValidityType.SINGLE_DAY,
        )
      }
    }

    this.selectedSellers = (this.form.sellers || []).slice()
    this.loading = false
  },
  validations() {
    return {
      organizationItemCategory: {
        name: { required },
        color: { required },
        priority: { required },
      },
    }
  },
  apollo: {
    getTicketSellers: {
      query: GET_TICKET_SELLERS,
      result({ data, loading }) {
        if (!loading) {
          const { getTicketSellers: users } = data
          this.availableSellers = users
          if (this.organizationSlug !== 'kk-cibona-ba9e') {
            this.availableSellers = this.availableSellers.filter(seller => seller.id !== '33')
          }
          this.tisak = users.find(u => u.name.toLowerCase() === tisakTicketSeller)
        }
      },
    },
    getCategories: {
      query: GET_ORGANIZATION_ITEM_CATEGORIES,
      manual: true,
      variables() {
        return {
          organizationSlug: this.organizationSlug,
          pagination: {
            page: 0,
            per_page: -1,
          },
        }
      },
      result({ data: { getOrganizationItemCategories }, loading }) {
        if (!loading) {
          this.categories = getOrganizationItemCategories.categories.map(category => {
            return {
              id: category.id,
              name: category.name,
              hexadecimal: category.color,
              priority: category.priority,
            }
          })
          this.organization = getOrganizationItemCategories.organization
          if (this.initialValues) {
            this.form.category = this.categories.find(
              c => c.id === this.initialValues.organizationItemCategoryId,
            )
          }
        }
      },
    },
  },
  methods: {
    handlePhysicalOutletsClick() {
      this.$set(this.form, 'physicalOutlets', !this.form.physicalOutlets)
      this.selectedSellers = []
      this.$set(this.form, 'sellers', [])
    },
    openKioskTerminalsInfo() {
      this.openKioskTerminalsInfoModal = true
    },
    onSelectAllClick() {
      this.toggleSelectAll()
      this.$nextTick(() => {
        if (this.$refs.selectSellers && this.$refs.selectSellers.$refs.menu) {
          this.$refs.selectSellers.$refs.menu.isActive = false
        }
      })
    },
    toggleSelectAll() {
      const ids = (this.availableSellers || []).map(s => s.id)
      const allSelected =
        ids.length > 0 && ids.every(id => (this.selectedSellers || []).includes(id))

      this.selectedSellers = allSelected ? [] : ids
      this.form.sellers = [...this.selectedSellers]
    },
    handleSelectChange(value) {
      if (!Array.isArray(value)) return
      if (value.includes('all')) {
        // if already all (plus 'all') -> clear all
        if (this.selectedSellers.length === (this.availableSellers || []).length + 1) {
          this.selectedSellers = []
        } else {
          // select all real seller ids
          this.selectedSellers = (this.availableSellers || []).map(s => s.id)
        }
      }
      // keep form.sellers in sync (without the 'all' token)
      this.form.sellers = this.selectedSellers.filter(v => v !== 'all')
    },
    handleOrganizationItemCategoryModalState() {
      this.organizationItemCategory = {}
      this.openModal = !this.openModal
    },
    savePromo() {
      this.promoCodeExists = true
      this.$emit('promoValue', this.promoCode)
    },
    deselectSeller(item) {
      this.form.sellers = (this.form.sellers || []).filter(sellerId => sellerId !== item.id)
      this.selectedSellers = (this.selectedSellers || []).filter(sellerId => sellerId !== item.id)
    },
    getDecimalFromRomanNumber(roman) {
      for (const number of Priorities) {
        if (number.roman === roman.toUpperCase()) return number.decimal
      }

      return 0
    },
    async handleCreateCategory(organizationItemCategory) {
      const { errors } = await this.$apollo.mutate({
        mutation: CREATE_ORGANIZATION_ITEM_CATEGORY,
        awaitRefetchQueries: true,
        refetchQueries: ['GetOrganizationItemCategories'],
        variables: {
          organizationSlug: this.organizationSlug,
          ...organizationItemCategory,
          color: organizationItemCategory.color.hexadecimal,
          priority: this.getDecimalFromRomanNumber(organizationItemCategory.priority),
        },
      })

      if (errors) {
        this.snackbar = {
          open: true,
          messageProp: errors[0].message,
          color: 'error',
        }
      } else {
        this.snackbar = {
          open: true,
          messageProp: 'workflowsAll.category_successfully_created_c795b2d',
          color: 'success',
        }
        this.openModal = false
        this.$v.$reset()
        this.organizationItemCategory = {}
      }
    },
    onUseAccessCodeChange() {
      if (!this.useAccessCode) {
        this.form.itemMeta.accessCode = undefined
      }
      this.$emit('useAccessCodeChange', this.useAccessCode)
    },
  },
}
</script>

<style scoped>
@media (max-width: 800px) {
  .pb-8 {
    margin-bottom: 1rem;
  }

  .pr-30,
  .pr-30 + * {
    margin-right: 0 !important;
  }
}

.pr-30 {
  margin-bottom: 1rem;
}

.price-disabled-description {
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 0.75rem;
  line-height: 1rem;
  color: #666666;
  margin-bottom: 1rem;
}

.tisak-term {
  font-family: 'Roboto';
  font-style: normal;
  font-weight: 400;
  font-size: 0.75rem;
  line-height: 1rem;
  color: #666666;
  text-indent: 1rem;
  margin-top: -0.75rem;
}
.outlets-text {
  color: var(--Primary-10, #0277bd);
  text-align: center;

  /* Body/Small */
  font-family: 'Source Sans Pro';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 150%; /* 24px */
}

.outlets-title {
  color: var(--Grey-10, #666);
  text-align: center;
  font-variant-numeric: lining-nums proportional-nums;

  /* Heading/H5 */
  font-family: Raleway;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 120%; /* 24px */
  letter-spacing: -0.6px;
}

.outlets-container {
  cursor: pointer;
  display: flex;
  padding: 24px 32px;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  gap: 16px;
  max-width: 50%;
  flex-shrink: 0;
  align-self: stretch;
  border-radius: 8px;
  border: 2px solid var(--Grey-30, #d9d9d9);
  background: var(--Grey-White, #fff);
}
.outlets-container-wrapper {
  display: flex;
  gap: 1rem;
  margin-bottom: 1rem;
}
@media (max-width: 580px) {
  .outlets-container-wrapper {
    flex-direction: column;
    gap: 1rem;
  }
  .outlets-container {
    max-width: 100%;
  }
}
.outlets-item {
  display: flex;
  height: 100%;
  background-color: white;
  padding: 24px;
  flex-direction: column;
  border-radius: 8px;
  background: #fff;
  justify-content: center;
  align-items: center;
  gap: 8px;
}
.outlets-items-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}
.category-row {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.color {
  width: 12px;
  height: 12px;
  border-radius: 10000px;
  margin-right: 0.5rem;
}

.access-code-container {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 24px;
}

.access-code-text {
  flex: 1;
  min-width: 0;
}

.access-code-title {
  color: #666666;
  font-size: 18px;
  font-family: 'Source Sans Pro';
  font-weight: 400;
  line-height: 21.6px;
  word-wrap: break-word;
  margin: 0;
}

.entry-date-title {
  color: var(--Grey-10, #666);

  /* Input/Large */
  font-family: 'Source Sans Pro';
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: 120%; /* 21.6px */
}

.entry-date-description {
  color: var(--Grey-10, #666);

  /* M3/body/small */
  font-family: Roboto;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px; /* 133.333% */
  letter-spacing: 0.4px;
}

.access-code-description {
  margin: 4px 0 0 0;
  color: #979797;
  font-size: 18px;
  font-family: 'Source Sans Pro';
  font-weight: 400;
  line-height: 21.6px;
  word-wrap: break-word;
}

::v-deep .access-code-switch .v-input--switch__track {
  color: #0277bd;
}

::v-deep .access-code-switch .v-input--switch__thumb {
  color: #ffffff;
}

::v-deep .access-code-field {
  margin: 0 0 24px 0;
}

::v-deep .access-code-field .v-messages {
  color: #666666;
}

::v-deep .display-flag-checkbox .v-messages {
  display: none;
}

.sport-membership-ticket-container {
  display: flex;
  justify-content: flex-start;
  align-items: flex-end;
  gap: 1rem;
}

.select-all-wrapper {
  width: 100%;
  cursor: pointer;
  padding: 8px 16px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
}

.select-all-wrapper:hover {
  background-color: #e3f2fd; /* Vuetify hover efekt */
}

.select-all-title {
  width: 100%;
  text-align: center;
}
</style>
